import React, { useState } from "react";
import { Combobox } from "@headlessui/react";
import { CheckIcon, SelectorIcon } from "@heroicons/react/solid";
import { useTranslation } from "react-i18next";

import EasyTrans from "../../bundel/EasyTrans";

/**
 * Searchable picker over the full SPDX license list (~600 entries). Type to
 * filter by name or id; the two special "Full Copyright" / "No Assertion"
 * entries are pinned at the top.
 */
const AllLicences = (props) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");

  const special = [
    {
      licenseId: "Full Copyright",
      name: t("RegisterWork.License.CommonRightsReserved"),
    },
    {
      licenseId: "No Assertion",
      name: t("RegisterWork.License.NoAssertion"),
    },
  ];
  const all = [...special, ...(props.licences || [])];
  const selected = all.find((l) => l.licenseId === props.licence) || null;

  const q = query.trim().toLowerCase();
  const filtered = (
    q === ""
      ? all
      : all.filter((l) =>
          `${l.name} ${l.licenseId}`.toLowerCase().includes(q),
        )
  ).slice(0, 80);

  const link = (props.licences || []).find(
    (l) => l.licenseId === props.licence,
  )?.detailsUrl;

  return (
    <div>
      <Combobox
        value={selected}
        onChange={(l) => {
          props.onSetLicence(l ? l.licenseId : "");
          props.setLicenceError(false);
          setQuery("");
        }}
      >
        <div className="relative">
          <div
            className={`relative w-full rounded-lg border bg-white shadow-sm ${
              props.licenceError ? "border-red-400" : "border-gray-300"
            }`}
          >
            <Combobox.Input
              className="w-full rounded-lg border-0 py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-fairblue-500"
              displayValue={(l) => l?.name || ""}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("RegisterWork.License.Search")}
            />
            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
              <SelectorIcon
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </Combobox.Button>
          </div>
          <Combobox.Options className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 text-sm shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-gray-500">
                <EasyTrans>RegisterWork.License.NoMatch</EasyTrans>
              </div>
            ) : (
              filtered.map((l) => (
                <Combobox.Option
                  key={l.referenceNumber ?? l.licenseId}
                  value={l}
                  className={({ active }) =>
                    `flex cursor-pointer items-center justify-between gap-2 px-3 py-2 ${
                      active ? "bg-fairblue-50 text-fairblue-700" : "text-gray-900"
                    }`
                  }
                >
                  {({ selected: isSel }) => (
                    <>
                      <span className={isSel ? "font-medium" : ""}>
                        {l.name}
                      </span>
                      {isSel && (
                        <CheckIcon
                          className="h-4 w-4 flex-shrink-0 text-fairblue-600"
                          aria-hidden="true"
                        />
                      )}
                    </>
                  )}
                </Combobox.Option>
              ))
            )}
          </Combobox.Options>
        </div>
      </Combobox>

      {link && (
        <a
          className="mt-2 inline-block text-sm text-fairblue-500 hover:underline"
          href={link}
          target="_blank"
          rel="noreferrer"
        >
          <EasyTrans>RegisterWork.License.FurtherInformation</EasyTrans>: {link}
        </a>
      )}
    </div>
  );
};

export default AllLicences;
